(function () {
  'use strict';


  // ═══════════════════════════════════════════════════════════════════════════
  // QUICK-CART HELPERS
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Opens the quick-cart by programmatically clicking the header cart icon.
   * The header's own JS listener fires r$1("quick-cart:open") — the internal
   * Fluorescent event that activates the drawer, focus trap, and scroll lock.
   */
  function openQuickCart() {
    var icon = document.querySelector('[data-js-cart-icon]');
    if (icon) icon.click();
  }

  /**
   * Triggers the theme's refreshQuickCart() by dispatching the DOM event it
   * already listens for on document.
   */
  function refreshQuickCart() {
    document.dispatchEvent(new CustomEvent('apps:product-added-to-cart'));
  }

  function quickCartExists() {
    return !!document.querySelector('.quick-cart');
  }

  var cartRefreshTimer = null;

  /**
   * Coalesce multiple add-to-cart signals (fetch, XHR, Rebuy events) into a
   * single quick-cart refresh so we don't hammer Shopify's cart endpoints.
   */
  function scheduleCartRefresh() {
    clearTimeout(cartRefreshTimer);
    cartRefreshTimer = setTimeout(function () {
      cartRefreshTimer = null;
      refreshQuickCart();
      openQuickCart();
    }, 500);
  }

  function parseCartJson(response) {
    if (!response) return Promise.resolve(null);

    if (response.status === 429) {
      console.warn('[felina] Cart API rate limited — skipping sync');
      return Promise.resolve(null);
    }

    if (!response.ok) return Promise.resolve(null);

    return response.json().catch(function () {
      return null;
    });
  }

  function isCartError(cart) {
    return !cart || cart.status || cart.message === 'Cart Error';
  }

  function getCartDiscountCodes(cart) {
    var codes = [];
    var raw = cart && cart.discount_codes;
    if (!raw || !raw.length) return codes;

    for (var i = 0; i < raw.length; i++) {
      var entry = raw[i];
      if (typeof entry === 'string') {
        codes.push(entry.toUpperCase());
      } else if (entry && entry.code) {
        codes.push(String(entry.code).toUpperCase());
      }
    }
    return codes;
  }

  /**
   * A code counts as accepted only when Shopify itself reports it on the cart as
   * applicable (cart.discount_codes[].applicable from /cart/update.js). Invalid,
   * expired or not-yet-active codes come back with applicable: false; unknown codes
   * are dropped from the list. Automatic discounts already on the cart must not
   * make a random word look valid.
   */
  function isDiscountAccepted(cart, code) {
    if (isCartError(cart) || !code) return false;

    var upper = code.toUpperCase();
    var entries = (cart && cart.discount_codes) || [];

    for (var i = 0; i < entries.length; i++) {
      var entry = entries[i];
      var entryCode = typeof entry === 'string' ? entry : (entry && entry.code) || '';
      if (String(entryCode).toUpperCase() !== upper) continue;
      if (typeof entry === 'string') return isDiscountApplied(cart, code);
      return entry.applicable === true;
    }

    // Older carts without discount_codes: fall back to a discount application carrying the code's title.
    if (!('discount_codes' in (cart || {}))) return isDiscountApplied(cart, code);
    return false;
  }

  function fetchCartSafe() {
    return fetch(cartApiUrl('cart.js')).then(parseCartJson);
  }

  function applyDiscountToCartSafe(code) {
    return fetch(cartApiUrl('cart/update.js'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ discount: code || '' })
    }).then(parseCartJson);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // 1. GLOBAL ADD-TO-CART BRIDGE
  //    Intercepts every /cart/add call — fetch(), XHR, and DOM events — so
  //    SearchTap, Rebuy, and third-party app add-to-cart buttons all open and
  //    refresh the quick-cart drawer automatically.
  // ═══════════════════════════════════════════════════════════════════════════

  // PDP-10: Track whether the shopper has explicitly selected a size on this page
  window.__userSelectedSizeManually = false;
  window.__initialGridReferralHandled = false;

  function isGridReferral() {
    if (window.__userSelectedSizeManually || window.__initialGridReferralHandled) return false;
    try {
      return new URLSearchParams(window.location.search).get('ref') === 'grid';
    } catch (e) {
      return false;
    }
  }
  window.isGridReferral = isGridReferral;

  function stripGridReferralFromUrl() {
    try {
      if (window.location.search && window.location.search.indexOf('ref=grid') !== -1) {
        var cleanUrl = new URL(window.location.href);
        cleanUrl.searchParams.delete('ref');
        var newSearch = cleanUrl.search ? cleanUrl.search : '';
        window.history.replaceState(window.history.state, '', cleanUrl.pathname + newSearch + cleanUrl.hash);
      }
    } catch (e) {}
  }
  window.stripGridReferralFromUrl = stripGridReferralFromUrl;

  var _originalFetch = window.fetch;
  window.fetch = function (url, options) {
    var promise = _originalFetch.apply(this, arguments);
    if (typeof url === 'string') {
      if (/\/cart\/add(\.js)?(\?|$)/.test(url)) {
        promise.then(function (res) {
          if (res && res.ok) scheduleCartRefresh();
          return res;
        }).catch(function () {});
      } else if (/\/products\/[^\/]+\.js(\?|$)/.test(url)) {
        promise.then(function () {
          setTimeout(function () {
            if (!window.__userSelectedSizeManually && !isGridReferral() && typeof window.runAllSizePreferenceRestorations === 'function') {
              window.runAllSizePreferenceRestorations();
            }
          }, 60);
          setTimeout(function () {
            if (!window.__userSelectedSizeManually && !isGridReferral() && typeof window.runAllSizePreferenceRestorations === 'function') {
              window.runAllSizePreferenceRestorations();
            }
          }, 300);
        }).catch(function () {});
      }
    }
    return promise;
  };

  var _origXHROpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    this._felinaUrl = typeof url === 'string' ? url : '';
    return _origXHROpen.apply(this, arguments);
  };

  var _origXHRSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.send = function () {
    var xhr = this;
    if (xhr._felinaUrl && /\/cart\/add(\.js)?(\?|$)/.test(xhr._felinaUrl)) {
      xhr.addEventListener('load', function () {
        if (xhr.status >= 200 && xhr.status < 300) scheduleCartRefresh();
      });
    }
    return _origXHRSend.apply(this, arguments);
  };

  // Some apps dispatch a "cart:item-added" DOM event after adding a product.
  document.addEventListener('cart:item-added', scheduleCartRefresh);

  // SearchTap add-to-cart buttons — coalesced with other add signals above.
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-st-add-to-cart], .st-atc-btn, [data-searchtap-add-to-cart]');
    if (!btn) return;
    scheduleCartRefresh();
  }, true);

  // ═══════════════════════════════════════════════════════════════════════════
  // 2. OPEN QUICK-CART ON ANY /cart LINK CLICK
  //    Intercepts <a href="/cart"> clicks outside the drawer and opens the
  //    drawer instead of navigating to the cart page.
  // ═══════════════════════════════════════════════════════════════════════════

  document.addEventListener('click', function (e) {
    if (!quickCartExists()) return;
    var link = e.target.closest('a');
    if (!link) return;
    if (link.hasAttribute('data-js-cart-icon')) return;
    if (link.closest('.quick-cart__container')) return;
    var href = link.getAttribute('href') || '';
    if (/^\/cart(\/?$|\?)/.test(href)) {
      e.preventDefault();
      openQuickCart();
    }
  }, true);

  // ═══════════════════════════════════════════════════════════════════════════
  // 3. PERSISTENT DISCOUNT CODE SYSTEM
  //
  //    Uses Shopify's Cart AJAX API (POST /cart/update.js with { discount })
  //    to apply codes in-cart, calculate real savings, and keep totals in sync.
  //    The code is also persisted in localStorage so it survives refresh and is
  //    re-applied after every Stiletto quick-cart section re-render.
  // ═══════════════════════════════════════════════════════════════════════════

  var DISCOUNT_KEY = 'felina_cart_discount';
  var discountSyncInFlight = null;
  var discountSyncTimer = null;
  var lastDiscountCartSnapshot = null;
  var DISCOUNT_SYNC_DEBOUNCE_MS = 900;

  function getStoredDiscount() {
    try {
      var v = localStorage.getItem(DISCOUNT_KEY);
      return v ? JSON.parse(v) : null;
    } catch (e) { return null; }
  }

  function setStoredDiscount(code) {
    try {
      localStorage.setItem(DISCOUNT_KEY, JSON.stringify({
        code: code.toUpperCase().trim(),
        appliedAt: Date.now()
      }));
    } catch (e) {}
  }

  function clearStoredDiscount() {
    try { localStorage.removeItem(DISCOUNT_KEY); } catch (e) {}
  }

  function cartApiUrl(path) {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root + path;
  }

  function formatMoney(cents) {
    if (window.Shopify && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(cents, window.Shopify.money_format);
    }
    return '$' + (cents / 100).toFixed(2);
  }

  function fetchCart() {
    return fetchCartSafe();
  }

  function applyDiscountToCart(code) {
    return applyDiscountToCartSafe(code);
  }

  function getCartSnapshot(cart) {
    if (!cart) return '';
    var stored = getStoredDiscount();
    return [
      cart.item_count || 0,
      cart.total_price || 0,
      cart.original_total_price || 0,
      stored && stored.code || ''
    ].join(':');
  }

  function isDiscountApplied(cart, code) {
    if (!code || !cart) return false;
    var upper = code.toUpperCase();

    var cartApps = cart.cart_level_discount_applications || [];
    if (cartApps.some(function (d) {
      return (d.title || '').toUpperCase() === upper;
    })) return true;

    var items = cart.items || [];
    for (var i = 0; i < items.length; i++) {
      var discounts = items[i].discounts || [];
      for (var j = 0; j < discounts.length; j++) {
        if ((discounts[j].title || '').toUpperCase() === upper) return true;
      }
      var allocs = items[i].line_level_discount_allocations || [];
      for (var k = 0; k < allocs.length; k++) {
        var title = allocs[k].discount_application && allocs[k].discount_application.title;
        if ((title || '').toUpperCase() === upper) return true;
      }
    }

    return false;
  }

  function getCartDiscountTotal(cart) {
    if (!cart) return 0;
    if (cart.total_discount > 0) return cart.total_discount;

    var apps = cart.cart_level_discount_applications || [];
    return apps.reduce(function (sum, d) {
      return sum + (d.total_allocated_amount || 0);
    }, 0);
  }

  function getSubtotalCurrencySuffix(cart) {
    var wrapper = document.querySelector('[data-cart-subtotal-wrapper]');
    if (!wrapper || !wrapper.hasAttribute('data-show-currency') || !cart || !cart.currency) return '';
    return ' ' + cart.currency;
  }

  function updateCartTotals(cart) {
    var subtotalEl = document.querySelector('[data-cart-subtotal]');
    if (!subtotalEl || !cart) return;

    var total = cart.total_price || 0;
    var original = cart.original_total_price || total;
    var currency = getSubtotalCurrencySuffix(cart);
    var stored = getStoredDiscount();
    var hasCodeDiscount = !!(stored && stored.code && original > total);

    if (hasCodeDiscount) {
      subtotalEl.innerHTML =
        '<s class="quick-cart__subtotal-compare">' + formatMoney(original) + currency + '</s>' +
        '<span class="sale-price">' + formatMoney(total) + currency + '</span>';
    } else {
      subtotalEl.textContent = formatMoney(total) + currency;
    }
  }

  /** Immediately drop strike-through markup when a code is removed. */
  function resetSubtotalDisplay(cart) {
    var subtotalEl = document.querySelector('[data-cart-subtotal]');
    if (!subtotalEl) return;

    var currency = cart ? getSubtotalCurrencySuffix(cart) : '';
    if (!currency) {
      var wrapper = document.querySelector('[data-cart-subtotal-wrapper]');
      if (wrapper && wrapper.hasAttribute('data-show-currency')) {
        var match = (subtotalEl.textContent || '').match(/\s([A-Z]{3})\s*$/);
        if (match) currency = ' ' + match[1];
      }
    }

    if (cart) {
      subtotalEl.textContent = formatMoney(cart.total_price || 0) + currency;
      return;
    }

    var compareEl = subtotalEl.querySelector('.quick-cart__subtotal-compare');
    if (compareEl) {
      subtotalEl.textContent = compareEl.textContent;
    }
  }

  function showFooterDiscountList() {
    var footer = document.querySelector('.quick-cart__footer-inner');
    if (!footer) return;
    footer.querySelectorAll('[data-cart-discount-list], .quick-cart__item-discounts').forEach(function (el) {
      el.classList.remove('hidden');
    });
  }

  function hideFooterDiscountList() {
    var footer = document.querySelector('.quick-cart__footer-inner');
    if (!footer) return;
    footer.querySelectorAll('[data-cart-discount-list], .quick-cart__item-discounts').forEach(function (el) {
      el.classList.add('hidden');
    });
  }

  function populateSaving(appliedState, cart) {
    var savingEl = appliedState.querySelector('[data-discount-saving]');
    if (!savingEl) return;

    var total = getCartDiscountTotal(cart);
    if (total > 0) {
      savingEl.textContent = 'You save ' + formatMoney(total);
    } else {
      savingEl.textContent = 'Discount applied at checkout';
    }
    savingEl.classList.remove('hidden');
  }

  function syncDiscountUI(cart) {
    var wrapper = document.querySelector('[data-discount-wrapper]');
    if (!wrapper) return Promise.resolve(cart);

    var inputState   = wrapper.querySelector('[data-discount-input-state]');
    var appliedState = wrapper.querySelector('[data-discount-applied-state]');
    if (!inputState || !appliedState) return Promise.resolve(cart);

    var stored = getStoredDiscount();

    if (stored && stored.code) {
      inputState.classList.add('hidden');
      appliedState.classList.remove('hidden');

      var codeLabel = appliedState.querySelector('[data-discount-code-label]');
      if (codeLabel) codeLabel.textContent = stored.code;

      populateSaving(appliedState, cart);
    } else {
      inputState.classList.remove('hidden');
      appliedState.classList.add('hidden');

      var errEl = wrapper.querySelector('[data-discount-error]');
      if (errEl) { errEl.textContent = ''; errEl.classList.add('hidden'); }
    }

    if (cart) updateCartTotals(cart);
    if (stored && stored.code) {
      hideFooterDiscountList();
    } else {
      showFooterDiscountList();
    }
    return Promise.resolve(cart);
  }

  /**
   * Re-validates the stored code against the current cart. Skips network calls
   * when nothing changed, and avoids re-posting /cart/update.js if the code is
   * already reflected on the cart.
   */
  function syncDiscountState() {
    var stored = getStoredDiscount();
    if (!stored || !stored.code) return Promise.resolve();

    if (discountSyncInFlight) return discountSyncInFlight;

    discountSyncInFlight = fetchCartSafe()
      .then(function (cart) {
        if (!cart) return null;

        if (!cart.item_count) {
          clearStoredDiscount();
          lastDiscountCartSnapshot = null;
          return syncDiscountUI(cart);
        }

        var snapshot = getCartSnapshot(cart);
        if (snapshot === lastDiscountCartSnapshot) {
          return syncDiscountUI(cart);
        }

        return applyDiscountToCartSafe(stored.code).then(function (updated) {
          if (updated && !isDiscountAccepted(updated, stored.code)) {
            clearStoredDiscount();
            lastDiscountCartSnapshot = null;
            return applyDiscountToCartSafe('').then(function (cleared) {
              return cleared || updated;
            });
          }
          return updated || cart;
        });
      })
      .then(function (cart) {
        if (!cart) return;
        lastDiscountCartSnapshot = getCartSnapshot(cart);
        return syncDiscountUI(cart);
      })
      .catch(function () {})
      .finally(function () {
        discountSyncInFlight = null;
      });

    return discountSyncInFlight;
  }

  function scheduleDiscountSync() {
    if (!getStoredDiscount()) return;
    clearTimeout(discountSyncTimer);
    discountSyncTimer = setTimeout(syncDiscountState, DISCOUNT_SYNC_DEBOUNCE_MS);
  }

  function showDiscountError(wrapper, message) {
    var errEl = wrapper.querySelector('[data-discount-error]');
    if (!errEl) return;
    errEl.textContent = message;
    errEl.classList.remove('hidden');
  }

  function setApplyLoading(btn, loading) {
    btn.classList.toggle('loading', loading);
    btn.setAttribute('aria-busy', loading ? 'true' : 'false');
    if (loading) {
      btn.setAttribute('aria-disabled', 'true');
    } else {
      btn.removeAttribute('aria-disabled');
    }
  }

  // ── Apply button ────────────────────────────────────────────────────────────

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-discount-apply]');
    if (!btn) return;

    var wrapper = btn.closest('[data-discount-wrapper]');
    if (!wrapper) return;

    var input   = wrapper.querySelector('[data-discount-input]');
    var errorEl = wrapper.querySelector('[data-discount-error]');
    if (!input) return;

    var code = (input.value || '').trim().toUpperCase();

    if (!code) {
      showDiscountError(wrapper, 'Please enter a discount code.');
      input.focus();
      return;
    }

    if (errorEl) { errorEl.textContent = ''; errorEl.classList.add('hidden'); }

    setApplyLoading(btn, true);

    applyDiscountToCart(code)
      .then(function (cart) {
        if (!isDiscountAccepted(cart, code)) {
          // leave the cart exactly as it was (a rejected code would otherwise sit on it as not applicable)
          return applyDiscountToCartSafe('').then(function () {
            throw new Error('Invalid discount code');
          });
        }
        return cart;
      })
      .then(function (cart) {
        setStoredDiscount(code);
        input.value = '';
        lastDiscountCartSnapshot = getCartSnapshot(cart);
        syncDiscountUI(cart);
        // re-render the drawer from Liquid (theme refresh event) so line prices, the free-shipping bar and the
        // total all reflect the discounted cart, not only the subtotal line we patch here
        if (typeof refreshQuickCart === 'function') refreshQuickCart();
      })
      .catch(function () {
        showDiscountError(wrapper, 'This code is invalid, expired or not active yet.');
      })
      .finally(function () {
        setApplyLoading(btn, false);
      });
  });

  // Enter key in the input triggers Apply.
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Enter') return;
    var input = e.target.closest('[data-discount-input]');
    if (!input) return;
    e.preventDefault();
    var btn = input.closest('[data-discount-wrapper]') &&
              input.closest('[data-discount-wrapper]').querySelector('[data-discount-apply]');
    if (btn) btn.click();
  });

  // ── Remove (×) button ───────────────────────────────────────────────────────

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-discount-remove]');
    if (!btn) return;

    clearStoredDiscount();
    lastDiscountCartSnapshot = null;
    resetSubtotalDisplay();
    syncDiscountUI();

    applyDiscountToCartSafe('')
      .then(fetchCartSafe)
      .then(function (cart) {
        if (cart) updateCartTotals(cart);
        syncDiscountUI(cart);
        if (typeof refreshQuickCart === 'function') refreshQuickCart();

        // One follow-up read in case Shopify clears the code a beat later.
        setTimeout(function () {
          if (getStoredDiscount()) return;
          fetchCartSafe().then(function (fresh) {
            if (fresh) syncDiscountUI(fresh);
          });
        }, 450);
      })
      .catch(function () {
        syncDiscountUI();
      });
  });

  // ── Checkout button intercept ───────────────────────────────────────────────
  // When a discount code is stored, redirect to /checkout?discount=CODE.

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('.quick-cart__submit');
    if (!btn) return;
    if (window.FelinaPurpleDotFallbackActive) return;

    var stored = getStoredDiscount();
    if (!stored || !stored.code) return;

    e.preventDefault();
    e.stopImmediatePropagation();
    window.location.href = '/checkout?discount=' + encodeURIComponent(stored.code);
  }, true);

  // ── Sync after Stiletto cart refresh ────────────────────────────────────────

  function observeCartRefresh() {
    var container = document.querySelector('.quick-cart__container');
    if (!container) return;

    var obs = new MutationObserver(function () {
      scheduleDiscountSync();
    });
    obs.observe(container, { childList: true, subtree: true });
  }

  // ── Sync when the drawer opens ───────────────────────────────────────────────

  function observeCartOpen() {
    var wrapper = document.querySelector('.quick-cart__wrapper');
    if (!wrapper) return;

    var obs = new MutationObserver(function () {
      if (wrapper.classList.contains('active')) {
        scheduleDiscountSync();
      }
    });
    obs.observe(wrapper, { attributes: true, attributeFilter: ['class'] });
  }

  // ── Bootstrap ────────────────────────────────────────────────────────────────

  function init() {
    if (getStoredDiscount()) syncDiscountState();
    observeCartRefresh();
    observeCartOpen();
  }

  // custom.js is loaded synchronously at the end of <body>, so the DOM is
  // already fully parsed. Use readyState check as a defensive guard.
  if (document.readyState !== 'loading') {
    init();
  } else {
    document.addEventListener('DOMContentLoaded', init);
  }

function normalizeAlphaSize(sizeStr) {
  if (!sizeStr || typeof sizeStr !== 'string') return '';
  var s = sizeStr.trim().toUpperCase();
  var map = {
    'EXTRA SMALL': 'XS',
    'X-SMALL': 'XS',
    'X SMALL': 'XS',
    'SMALL': 'S',
    'MEDIUM': 'M',
    'LARGE': 'L',
    'EXTRA LARGE': 'XL',
    'X-LARGE': 'XL',
    'X LARGE': 'XL',
    'XX-LARGE': '2X',
    'XX LARGE': '2X',
    'XXL': '2X',
    '2XL': '2X',
    '2X-LARGE': '2X',
    '3XL': '3X',
    '3X-LARGE': '3X',
    '1XL': '1X',
    '1X': '1X',
    '2X': '2X',
    '3X': '3X',
    '4X': '4X',
    'ONE SIZE': 'OS',
    'O/S': 'OS'
  };
  if (map[s]) return map[s];
  var prefixMatch = s.match(/^(XS|S|M|L|XL|XXL|1X|2X|3X|4X)\b/i);
  if (prefixMatch) return prefixMatch[1].toUpperCase();
  return s;
}

function sizesMatch(sizeA, sizeB) {
  if (!sizeA || !sizeB) return false;
  var strA = String(sizeA).trim();
  var strB = String(sizeB).trim();
  if (strA.toLowerCase() === strB.toLowerCase()) return true;

  var normA = normalizeAlphaSize(strA);
  var normB = normalizeAlphaSize(strB);
  if (normA && normB && normA === normB) return true;

  if ((strA.includes('/') || strB.includes('/')) && (normA || normB)) {
    var partsA = strA.split('/').map(function(p) { return normalizeAlphaSize(p); });
    var partsB = strB.split('/').map(function(p) { return normalizeAlphaSize(p); });
    if (partsA.includes(normB) || partsB.includes(normA)) return true;
  }
  return false;
}

function getNormalizedSizeCategory(productType) {
  productType = (productType || '').toLowerCase();
  var hasBandChips = typeof $ !== 'undefined' ? $('.product__chip[data-type="band"]').length > 0 : document.querySelector('.product__chip[data-type="band"]');
  if (productType.includes('bra') && hasBandChips) {
    return 'bra';
  } else if (productType.includes('bra')) {
    return 'simple_bra';
  } else if (
    productType.includes('panty') ||
    productType.includes('panties') ||
    productType.includes('thong') ||
    productType.includes('brief') ||
    productType.includes('hipster') ||
    productType.includes('bikini') ||
    productType.includes('hi-cut') ||
    productType.includes('boyleg') ||
    productType.includes('underwear')
  ) {
    return 'panty';
  }
  return 'apparel';
}

function getBraChipValue($chip) {
  return $chip.attr('data-value') || $chip.attr('data-option-value') || '';
}

// A quick view fetches a whole other product's markup on top of whatever page it was
// opened from. On a PDP with related/cross-sell products, that means TWO copies of
// #currproduct-type, [data-variant-json], .product-form__cart-submit etc. sit in the
// document at once. Every lookup below used to be a bare $(selector) — which matches
// across BOTH products — so band/cup availability was computed from a JSON blob that
// was actually two products' variant arrays concatenated (a JSON.parse the try/catch
// silently swallowed), and "is everything selected" was checked against the background
// page's own untouched size chips. Result: a quick-view-only impossible combo (e.g. a
// bra's 32+B) looked like a normal, fully-selected size with no error message. Scoping
// every query to the open modal (falling back to the page itself when none is open)
// fixes that at the source instead of chasing each symptom.
function braScope() {
  var modal = document.querySelector('[data-quick-view-modal]');
  return (modal && modal.classList.contains('is-visible')) ? modal : document;
}

function findBraOptionSelect(namePattern, scope) {
  return $(scope || document).find('select[name^="options["]').filter(function() {
    return namePattern.test($(this).attr('name') || '');
  }).first();
}

function getBraSizeContext() {
  var scope = braScope();
  var $combinedBand = $(scope).find('.product__chip[data-type="band"]');
  var $combinedCup = $(scope).find('.product__chip[data-type="cup"]');
  if ($combinedBand.length && $combinedCup.length) {
    return {
      mode: 'combined',
      $bandChips: $combinedBand,
      $cupChips: $combinedCup,
      bandKey: null,
      cupKey: null
    };
  }

  var $bandSelect = findBraOptionSelect(/options\[band\s*size\]/i, scope);
  var $cupSelect = findBraOptionSelect(/options\[cup\s*size\]/i, scope);
  if ($bandSelect.length && $cupSelect.length) {
    return {
      mode: 'separated',
      $bandChips: $bandSelect.closest('[data-product-option], .product__option').find('.product__chip'),
      $cupChips: $cupSelect.closest('[data-product-option], .product__option').find('.product__chip'),
      bandKey: $bandSelect.attr('id') || $bandSelect.attr('data-index'),
      cupKey: $cupSelect.attr('id') || $cupSelect.attr('data-index')
    };
  }

  return null;
}

function findBraSizeVariant(colorVariants, bandValue, cupValue, context, hasColor) {
  return colorVariants.find(function(variant) {
    if (context.mode === 'separated') {
      return String(variant[context.bandKey]) === String(bandValue) &&
             String(variant[context.cupKey]) === String(cupValue);
    }

    var combinedSize = String(bandValue) + String(cupValue);
    var variantSize = hasColor ? variant.option2 : variant.option1;
    return variantSize === combinedSize;
  });
}

function getInventoryMap() {
  var el = $('[data-product-inventory-json]');
  if (!el.length) return null;
  try {
    var data = JSON.parse(el.text());
    return data.inventory || null;
  } catch (e) {
    return null;
  }
}

// Defer to Shopify's own availability rule: a variant is sellable when inventory is
// untracked, when the policy allows overselling, or when there is stock on hand. Do
// not second-guess it from inventory_quantity — an untracked variant reports zero and
// is still purchasable.
function isBraVariantAvailable(variant) {
  return !!(variant && variant.available);
}

// Keep Shopify's accelerated checkout in sync with the variant stored in the
// product form. The wallet component loads independently from the theme, so an
// automatic size restoration can finish before Shopify starts listening for
// form changes.
function syncAcceleratedCheckoutVariant(form, retries) {
  if (!form) return;

  var variantSelector = form.querySelector('[name="id"]');
  var acceleratedCheckout = form.querySelector('shopify-accelerated-checkout');
  var remainingRetries = typeof retries === 'number' ? retries : 0;

  if (!variantSelector || !variantSelector.value) return;

  if (!acceleratedCheckout || !acceleratedCheckout.firstElementChild) {
    if (remainingRetries > 0) {
      setTimeout(function () {
        syncAcceleratedCheckoutVariant(form, remainingRetries - 1);
      }, 250);
    }
    return;
  }

  form.dispatchEvent(new Event('change', { bubbles: true }));
}

function syncAllAcceleratedCheckoutVariants(retries) {
  document.querySelectorAll('form[data-product-form]').forEach(function (form) {
    syncAcceleratedCheckoutVariant(form, retries);
  });
}

function syncInventoryForVariant(variant) {
  var $counter = $('[data-inventory-counter]').first();
  var inventoryMap = getInventoryMap();
  var $selector = $('#variant-selector');

  if ($selector.length && variant && variant.id && $selector.val() !== String(variant.id)) {
    $selector.val(String(variant.id));
    if ($selector[0]) {
      $selector[0].dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  if (!$counter.length) return;

  var $msg = $counter.find('.inventory-counter__message');
  var $bar = $counter.find('.inventory-counter__bar-progress');
  var unavailableText = $counter.data('unavailable-text') || 'Unavailable';

  $counter.removeClass('hidden inventory--unavailable inventory--empty inventory--low');

  if (!variant) {
    $counter.addClass('inventory--unavailable');
    $counter.attr('data-stock-category', 'unavailable');
    $msg.text(unavailableText);
    $bar.css('width', '0%');
    return;
  }

  var inv = inventoryMap && (inventoryMap[variant.id] || inventoryMap[String(variant.id)]);

  if (!isBraVariantAvailable(variant)) {
    $counter.addClass('inventory--empty');
    $counter.attr('data-stock-category', 'empty');
    $msg.text((inv && inv.inventory_message) || $counter.data('sold-out-text') || 'Out of stock');
    $bar.css('width', '0%');
    return;
  }

  $counter.attr('data-stock-category', 'sufficient');
  $msg.text((inv && inv.inventory_message) || '');
  if (inv && Number(inv.inventory_quantity) > 0) {
    var max = parseInt($counter.data('stock-countdown-max'), 10) || 20;
    var pct = Math.min(100, (Number(inv.inventory_quantity) / max) * 100);
    $bar.css('width', pct + '%');
  }
}

// Disable band/cup chips if all corresponding combinations are unavailable
function updateBraSizeAvailability() {
  var scope = braScope();

  // Only run for bra products
  if (!$(scope).find('#currproduct-type[value*="Bra"]').length) return;

  var context = getBraSizeContext();
  if (!context || !context.$bandChips.length || !context.$cupChips.length) return;

  // Check if product has a color option
  var hasColor = $(scope).find('.product__variants-wrapper').data('has-color');
  if (hasColor === undefined) {
    hasColor = $(scope).find('.product__color-swatch, .product__chip[data-type="color"]').length > 0;
  }

  // Get the currently selected color if applicable
  var selectedColor = null;
  if (hasColor) {
    selectedColor = $(scope).find('#option1').val();
    if (!selectedColor) {
      // On page load, the select might not be updated yet, try to get from swatch
      var $selectedSwatch = $(scope).find('.product__color-swatch.selected, .product__chip.selected[data-type="color"]');
      if ($selectedSwatch.length) {
        selectedColor = $selectedSwatch.data('value') || $selectedSwatch.attr('data-option-value');
      } else {
        return;
      }
    }
  }

  // Get all product variants from JSON
  var variantsJsonEl = $(scope).find('[data-variant-json]');
  if (!variantsJsonEl.length) return;

  try {
    var variants = JSON.parse(variantsJsonEl.text());

  // Filter variants to only those matching the selected color (only if color option exists)
  var colorVariants = variants;
  if (hasColor) {
    colorVariants = variants.filter(function(variant) {
      return variant.option1 === selectedColor;
    });
  }

    var $bandChips = context.$bandChips;
    var $cupChips = context.$cupChips;

    // Check if a specific band or cup is currently selected
    var $selectedBand = $bandChips.filter('.selected');
    var $selectedCup = $cupChips.filter('.selected');
    var selectedBandValue = $selectedBand.length ? getBraChipValue($selectedBand) : null;
    var selectedCupValue = $selectedCup.length ? getBraChipValue($selectedCup) : null;

    // For each band, check availability
    $bandChips.each(function() {
      var bandValue = getBraChipValue($(this));
      var hasAvailableCup = false;

      // If a cup is selected, only check that specific cup
      if (selectedCupValue) {
        var matchingVariant = findBraSizeVariant(colorVariants, bandValue, selectedCupValue, context, hasColor);
        if (isBraVariantAvailable(matchingVariant)) {
          hasAvailableCup = true;
        }
      } else {
        // No cup selected, check if ANY cup combination is available
        $cupChips.each(function() {
          var cupValue = getBraChipValue($(this));
          var matchingVariant = findBraSizeVariant(colorVariants, bandValue, cupValue, context, hasColor);

          if (isBraVariantAvailable(matchingVariant)) {
            hasAvailableCup = true;
            return false; // break out of loop
          }
        });
      }

      // If no available cups for this band, disable the band chip
      if (!hasAvailableCup) {
        $(this).addClass('disabled');
      } else {
        $(this).removeClass('disabled');
      }
    });

    // For each cup, check availability
    $cupChips.each(function() {
      var cupValue = getBraChipValue($(this));
      var hasAvailableBand = false;

      // If a band is selected, only check that specific band
      if (selectedBandValue) {
        var matchingVariant = findBraSizeVariant(colorVariants, selectedBandValue, cupValue, context, hasColor);
        if (isBraVariantAvailable(matchingVariant)) {
          hasAvailableBand = true;
        }
      } else {
        // No band selected, check if ANY band combination is available
        $bandChips.each(function() {
          var bandValue = getBraChipValue($(this));
          var matchingVariant = findBraSizeVariant(colorVariants, bandValue, cupValue, context, hasColor);

          if (isBraVariantAvailable(matchingVariant)) {
            hasAvailableBand = true;
            return false; // break out of loop
          }
        });
      }

      // If no available bands for this cup, disable the cup chip
      if (!hasAvailableBand) {
        $(this).addClass('disabled');
      } else {
        $(this).removeClass('disabled');
      }
    });

    // Colour swatches are left untouched by the theme's own availability pass, which
    // only ever cascades downwards from option1. Cross out any colour that cannot
    // supply the band + cup the shopper has chosen.
    if (hasColor && selectedBandValue && selectedCupValue) {
      $(scope).find('.product__color-swatch, .product__chip[data-type="color"]').each(function() {
        var $swatch = $(this);
        var swatchColor = $swatch.attr('data-option-value') || $swatch.attr('data-value');
        if (!swatchColor) return;

        var variantsForColor = variants.filter(function(variant) {
          return variant.option1 === swatchColor;
        });
        var swatchVariant = findBraSizeVariant(variantsForColor, selectedBandValue, selectedCupValue, context, hasColor);
        $swatch.toggleClass('disabled', !isBraVariantAvailable(swatchVariant));
      });
    }

    var selectedVariant = null;
    if (selectedBandValue && selectedCupValue) {
      selectedVariant = findBraSizeVariant(colorVariants, selectedBandValue, selectedCupValue, context, hasColor);
    }
    syncInventoryForVariant(selectedVariant);
  } catch(e) {
    console.error('Error in updateBraSizeAvailability:', e);
  }
}

// Add debounce flag to prevent infinite loops
var manageErrorTimeout = null;

function manageError() {
  // Debounce to prevent infinite loops
  if (manageErrorTimeout) {
    clearTimeout(manageErrorTimeout);
  }

  manageErrorTimeout = setTimeout(function() {
    executeManageError();
    manageErrorTimeout = null;
  }, 50);
}

function executeManageError() {
  var scope = braScope();
  var allSelected = true;
  var isComplexBra = $(scope).find('#currproduct-type[value*="Bra"]').length > 0 && getBraSizeContext() !== null;

    if(isComplexBra){
      $(scope).find('.product__color-chips:not(.chips2-wrap)').each(function(){
        // Only validate if this container actually has chips
        if ($(this).find('.product__chip').length > 0) {
          if ($(this).find('.product__chip.selected').length === 0) {
            allSelected = false; return false;
          }
        }
      });
    }
    else{
      $(scope).find('.product__color-chips').each(function(){
        // Only validate if this container actually has chips
        if ($(this).find('.product__chip').length > 0) {
          if ($(this).find('.product__chip.selected').length === 0) {
            allSelected = false; return false;
          }
        }
      });
    }

    // Check if the currently selected variant combination is actually available
    var variantIsAvailable = true;
    var matchingVariantExists = false;
    if (allSelected) {
      // Get all product variants from the JSON data
      var variantsJsonEl = $(scope).find('[data-variant-json]');
      if (variantsJsonEl.length) {
        try {
          var variants = JSON.parse(variantsJsonEl.text());

          var braContext = getBraSizeContext();

          // For bra products with band/cup chips (combined or separated), check that combo
          if(braContext) {
            var selectedBand = getBraChipValue(braContext.$bandChips.filter('.selected'));
            var selectedCup = getBraChipValue(braContext.$cupChips.filter('.selected'));
            var selectedColor = $(scope).find('.product__chip[data-type="color"].selected').data('value') ||
              $(scope).find('.product__color-swatch.selected').attr('data-option-value') ||
              $(scope).find('#option1').val();

            var hasColor = $(scope).find('.product__variants-wrapper').data('has-color');
            if (hasColor === undefined) {
              hasColor = $(scope).find('.product__color-swatch, .product__chip[data-type="color"]').length > 0;
            }

            var colorVariants = variants;
            if (hasColor) {
              colorVariants = variants.filter(function(variant) {
                return variant.option1 === selectedColor;
              });
            }

            var matchingVariant = findBraSizeVariant(colorVariants, selectedBand, selectedCup, braContext, hasColor);

            if (matchingVariant) {
              matchingVariantExists = true;
              variantIsAvailable = isBraVariantAvailable(matchingVariant);
              syncInventoryForVariant(matchingVariant);
            } else {
              matchingVariantExists = false;
              variantIsAvailable = false;
              syncInventoryForVariant(null);
            }
          } else {
            // For non-bra products, find the variant from the options the shopper picked.
            // When a color doesn't come in the chosen size, the theme leaves #variant-selector
            // on the last real variant, so reading it here turned Add to Cart back on and
            // added that previous color to the cart instead.
            var pickedOptions = [];
            $(scope).find('select[id^="option"][name^="options["]').each(function() {
              var position = parseInt(this.id.replace('option', ''), 10);
              if (position) pickedOptions[position - 1] = $(this).val();
            });

            var matchingVariant;
            if (pickedOptions.length) {
              matchingVariant = variants.find(function(variant) {
                return pickedOptions.every(function(value, i) {
                  return value == null || variant['option' + (i + 1)] === value;
                });
              }) || null;
            } else {
              var selectedVariantId = $(scope).find('#variant-selector').val();
              if (selectedVariantId) {
                matchingVariant = variants.find(function(variant) {
                  return variant.id == selectedVariantId;
                }) || null;
              }
            }

            if (matchingVariant) {
              matchingVariantExists = true;
              variantIsAvailable = isBraVariantAvailable(matchingVariant);
            } else if (matchingVariant === null) {
              matchingVariantExists = false;
              variantIsAvailable = false;
            }
          }
        } catch(e) {
          console.error('Error parsing variant JSON:', e);
        }
      }
    }

    // Update button state and text
    var $submitButton = $(scope).find('.product-form__cart-submit');
    var $buttonText = $submitButton.find('[data-add-to-cart-text]');



    if(allSelected && variantIsAvailable){
      // Enable button and show "Add to cart" text
      $submitButton.removeAttr('disabled').removeAttr('title');
      if($buttonText.length) {
        var langAvailable = $submitButton.data('lang-available') || 'Add to cart';
        $buttonText.text(langAvailable);
      }
    } else {
      // Disable button and update text
      var disabledMessage = allSelected && !variantIsAvailable ? 'This combination is not available' : 'Please select an option from every group';
      $submitButton.attr('disabled', 'disabled').attr('title', disabledMessage);

      if($buttonText.length) {
        if(allSelected && !variantIsAvailable) {
          // Show "Sold out" for unavailable combinations
          var langSoldOut = $submitButton.data('lang-sold-out') || 'Sold out';
          $buttonText.text(langSoldOut);
        } else {
          // Incomplete selection: keep the Add to Cart label (the button is disabled and the
          // "Please Select A Size" message explains why), as the live theme does.
          var langAvailableIdle = $submitButton.data('lang-available') || 'Add to cart';
          $buttonText.text(langAvailableIdle);
        }
      }
    }

    // Handle error messages for bra products
    if(isComplexBra){
      // PDP-10: bras no longer arrive with a band and cup already chosen, so prompt for
      // whichever half is still missing instead of leaving a disabled button unexplained.
      var bandChosen = $(scope).find('.product__chip[data-type="band"].selected').length > 0;
      var cupChosen = $(scope).find('.product__chip[data-type="cup"].selected').length > 0;
      // A band+cup combo can be individually clickable (each half pairs with *some* other
      // value) while the two together don't exist for this color — e.g. 32 + B when only
      // 32D-32H and 34B-38B exist. The chips do get a distinct greyed/struck-through style
      // for this (see .product__chip.selected.disabled in custom.css.liquid) and the button
      // itself switches to "Sold out", but both are easy to miss, so spell it out here too.
      var comboUnavailable = bandChosen && cupChosen && !variantIsAvailable;
      $(scope).find('[data-band-error]').toggle(!bandChosen);
      $(scope).find('[data-cup-error]').toggle(!cupChosen);
      $(scope).find('[data-combo-error]').toggle(comboUnavailable);
      $(scope).find('[no-size-error]').hide();
    } else {
      // For non-bra products, use the generic error
      // only prompt when the product actually offers size chips — gift cards, socks sold by
      // denomination and other chipless products were showing "Please Select A Size"
      // permanently while Add to Cart worked (PDP-10)
      if($(scope).find('.product__chip').length > 0 && $(scope).find('.product__chip.selected').length == 0){
        $(scope).find('[no-size-error]').show();
      } else {
        $(scope).find('[no-size-error]').hide();
      }

      // Hide bra-specific errors
      $(scope).find('[data-band-error]').hide();
      $(scope).find('[data-cup-error]').hide();
      $(scope).find('[data-combo-error]').hide();
    }
    // Show/hide the Klaviyo BIS trigger to match the selected variant. Use .css('display', ...)
    // rather than jQuery .hide()/.show()/.toggle(): those cache the element's *computed*
    // display the first time they hide it and always restore that exact cached value, so if
    // the very first hide happens to run before the button's "btn" classes (display: flex)
    // have taken effect, it locks onto "inline" forever after. overflow:hidden has no effect
    // on a display:inline box, so the trigger's skewed ::before hover-fill pseudo-element
    // (normally clipped, invisible) spills out unclipped into a slanted ribbon shape.
    // Setting/clearing the inline style directly avoids that caching bug entirely.
    var shouldShowKlaviyo = allSelected && matchingVariantExists && !variantIsAvailable;
    $(scope).find('.klaviyo-bis-trigger, .klaviyo-bis-trigger-btn').css('display', shouldShowKlaviyo ? '' : 'none');
}

jQuery(document).ready(function ($) {

  // Quick view: capture phase so (1) injected Searchtap rows work and (2) the event never
  // reaches duplicate listeners theme ProductItem added on first-paint buttons (avoids double-open).
  document.addEventListener(
    'click',
    function (e) {
      var link = e.target.closest && e.target.closest('.show-product-quickview');
      if (!link || !document.documentElement.contains(link)) return;
      e.preventDefault();
      e.stopPropagation();
      // Searchspring-rendered rows put the product URL in `href`; the theme's own
      // product-item markup only sets `data-product-url` (no `href`). Without this
      // fallback, clicking Quick View on any native grid (featured collections,
      // cross-sells, recommended/recently-viewed products) silently did nothing:
      // this listener still preventDefault/stopPropagation'd the click, but never
      // had a URL to open, and stopPropagation kept theme.js's own ProductItem
      // click handler (which reads `dataset.productUrl` correctly) from ever running.
      var url = link.getAttribute('href') || link.dataset.productUrl;
      if (url && typeof window.openQuickView === 'function') {
        // A quick view is a fresh product, so whatever the shopper chose before this click
        // must not stop the new modal's preselected size from being cleared.
        unlockSizeChoice();
        window.openQuickView(url);
        // The quick view is fetched from the card's own ?variant= url, so its markup
        // arrives with a size already chosen. Mark the modal as grid-referred and let the
        // watcher below clear it whenever its chips appear. (PDP-10)
        //
        // This used to install a fresh observer per click and tear it down on a 10 second
        // timer. The modal is an XHR of the whole product page — measured open-to-render
        // times ranged from 3 to 31 seconds — so the timer regularly won the race, the
        // card's size was never cleared, and the modal opened with Add to Cart live on a
        // size the shopper had not picked. Nothing is timed now: the watcher lives as long
        // as the modal does.
        var modal = document.querySelector('[data-quick-view-modal]');
        if (modal) {
          if (/[?&]ref=grid/.test(url)) {
            modal.dataset.refGrid = '1';
          } else {
            delete modal.dataset.refGrid;
          }
          watchQuickView(modal);
        }
      }
    },
    true
  );

  $('.fs-product-card-title').matchHeight();
  if ($('.flair-badge').length > 0) {
    $('.flair-badge-layout').matchHeight();
    $('.fs-product-card-title').matchHeight();
  }

  // Flair badges: don't restyle Flair's shadow DOM from here. The old flush-left
  // injection zeroed every badge's padding, so the text looked cropped, and it only
  // ran on the next DOM change, so badges flashed from padded to cropped. Flair's
  // layout already sits flush left. Long badges wrap via custom.css.liquid.

  // Handle closing Athos mobile filter drawer via Apply button
  $(document).on('click', '.ss__sidebar__mobile-apply', function(e) {
    e.preventDefault();
    document.body.classList.remove('athos-filters-open');
    var sidebar = document.getElementById('athos-sidebar');
    if (sidebar) sidebar.setAttribute('aria-expanded', 'false');
  });

  // Handle closing Athos mobile search overlay via Cancel / Close button
  $(document).on('click', '.quick-search__close, [data-close-icon]', function(e) {
    var quickSearch = document.getElementById('MainQuickSearch');
    if (quickSearch && quickSearch.classList.contains('quick-search--visible')) {
      quickSearch.classList.remove('quick-search--visible');
      quickSearch.setAttribute('aria-hidden', 'true');
      document.body.setAttribute('quick-search-open', 'false');
      document.body.setAttribute('data-fluorescent-overlay-open', 'false');
      document.body.classList.remove('quick-search-open');
      var searchIcon = document.querySelector('.header__icon-touch--search');
      if (searchIcon) searchIcon.setAttribute('aria-expanded', 'false');
    }
  });

  /*
    PDP-10. Product cards in the Athos grid link to the PDP with the card's colour as
    ?variant=…, which makes Shopify set product.selected_variant, which in turn makes the
    variant picker preselect that variant's SIZE. So the ordinary path (grid -> product)
    always landed on a preselected size even though a clean product URL does not.
    The card links now carry ref=grid (see felina-athos Results.jsx buildProductUrl): keep
    the colour those links choose, drop the size, and do not restore a remembered size on
    that navigation. A shopper's own shared ?variant= link has no ref=grid and still
    preselects everything (PDP-24).
  */
  function isGridReferral() {
    if (window.__userSelectedSizeManually || window.__initialGridReferralHandled) return false;
    try {
      return new URLSearchParams(window.location.search).get('ref') === 'grid';
    } catch (e) {
      return false;
    }
  }
  window.isGridReferral = isGridReferral;

  /*
    PDP-10 follow-up. The clear above is scheduled from several places that also fire AFTER
    the shopper has picked a size: the /products/<handle>.js fetch hook at the top of this
    file runs on every option change, and the window.load passes can land seconds late on a
    page carrying this much third-party script. What Charlotte saw was a size chip that
    selected and then deselected a moment later, with Add to Cart still disabled. The first
    deliberate size interaction closes the window for good. Opening a quick view reopens it,
    because that is a different product arriving with its own preselected size.
  */
  var sizeChoiceLocked = false;

  function lockSizeChoice() {
    sizeChoiceLocked = true;
  }

  function unlockSizeChoice() {
    sizeChoiceLocked = false;
  }

  window.lockSizeChoice = lockSizeChoice;
  window.unlockSizeChoice = unlockSizeChoice;

  /*
    The quick view modal is reused for every product, so a single watcher for its whole life
    is enough — and unlike a per-click observer on a timer, it cannot lose the race against
    a modal that takes half a minute to render. It clears the card's preselected size as
    soon as the modal's chips appear and keeps doing so until the shopper picks one, which
    sets the lock, or until a non-grid modal replaces it.
  */
  var quickViewWatched = false;
  var clearingModal = false;

  function watchQuickView(modal) {
    if (quickViewWatched || !window.MutationObserver) return;
    quickViewWatched = true;
    new MutationObserver(function () {
      if (clearingModal) return;
      if (modal.dataset.refGrid !== '1') return;
      if (!modal.querySelector('.product__chip')) return;
      clearingModal = true;
      try {
        clearSizeSelection(modal);
      } finally {
        clearingModal = false;
      }
    }).observe(modal, { childList: true, subtree: true });
  }

  // A quick view opened from a grid card owns the size question while it is on screen, so
  // the page-level restore must not reach into it. isGridReferral() reads window.location,
  // which on a collection page never carries ref=grid — only the card's href does — so the
  // restore branch was writing a remembered size back into a modal that had just been
  // correctly cleared.
  function activeGridModal() {
    var modal = document.querySelector('[data-quick-view-modal]');
    if (!modal || modal.dataset.refGrid !== '1') return null;
    return modal.offsetParent === null ? null : modal;
  }

  // Capture phase, so the lock is set before the theme's own handler kicks off the variant
  // fetch whose completion callback would otherwise clear the selection 60ms later.
  document.addEventListener(
    'click',
    function (e) {
      var chip = e.target && e.target.closest ? e.target.closest('.product__chip') : null;
      if (chip && chip.getAttribute('data-type') !== 'color') {
        lockSizeChoice();
      }
    },
    true
  );

  document.addEventListener(
    'change',
    function (e) {
      var field = e.target;
      if (!field || !field.closest) return;
      var option = field.closest('[data-product-option], .product__option');
      if (!option) return;
      var label = ($(option).find('label').first().text() || '').toLowerCase();
      if (label.indexOf('size') !== -1 || label.indexOf('band') !== -1 || label.indexOf('cup') !== -1) {
        lockSizeChoice();
      }
    },
    true
  );

  function clearSizeSelection(root) {
    // `&& !root` used to exempt scoped calls from the lock, which defeated the whole point of
    // the lock for the one caller that always passes a root: watchQuickView's MutationObserver
    // (below) calls clearSizeSelection(modal) on every DOM mutation inside the modal — including
    // the mutation caused by the shopper's own click updating the band/cup label text. With the
    // lock exempted here, that observer immediately wiped the selection the shopper had just
    // made, so band/cup chips looked unclickable in a grid-referred quick view (PDP-10).
    if (sizeChoiceLocked || window.__userSelectedSizeManually) return;
    var $scope = root ? $(root) : $(document);
    $scope.find('.product__chip.selected').not('[data-type="color"]').removeClass('selected');
    $scope.find('[data-bandsize-value], [data-cupsize-value]').text('');
    $scope.find('.product__option').each(function () {
      var label = ($(this).find('label').text() || '').toLowerCase();
      if (label.indexOf('size') !== -1) {
        $(this).find('[data-selected-value-for-option]').text('');
      }
    });
    manageError();
  }
  window.clearSizeSelection = clearSizeSelection;

  function restoreBraSizePreference() {
    var scope = braScope();
    if (!$(scope).find('#currproduct-type[value*="Bra"]').length && !$(scope).find('.product__chip[data-type="band"]').length) {
      return;
    }

    var savedPreference =
      localStorage.getItem('felina_bra_size_preference') ||
      localStorage.getItem('bra_size_preference') ||
      localStorage.getItem('bras') ||
      localStorage.getItem('bra');
    var preferredBand = null;
    var preferredCup = null;

    if (savedPreference) {
      try {
        var preference =
          typeof savedPreference === 'string' && (savedPreference.startsWith('{') || savedPreference.startsWith('['))
            ? JSON.parse(savedPreference)
            : savedPreference;
        if (preference && typeof preference === 'object') {
          preferredBand = preference.band;
          preferredCup = preference.cup;
        }
      } catch (e) {
        console.error('Error parsing bra size preference:', e);
      }
    }

    var selectedVariantId = $(scope).find('#variant-selector').val() || $(scope).find('select[name="id"]').val();
    var deepLinkVariantId = null;
    var isDeepLink = false;
    try {
      var params = new URLSearchParams(window.location.search);
      deepLinkVariantId = params.get('variant');
      isDeepLink = !!deepLinkVariantId && params.get('ref') !== 'grid';
    } catch (e) {}
    var variantsJsonEl = $(scope).find('[data-variant-json]');

    if (variantsJsonEl.length) {
      try {
        var variants = JSON.parse(variantsJsonEl.text());
        // An explicit shared/deep-linked variant must win over the remembered size.
        // Otherwise repeated preference restoration can replace an in-stock URL variant
        // with a different (and potentially sold-out) size after Shopify Pay initializes.
        var selectedVariant = variants.find(function (v) {
          return v.id == (isDeepLink ? deepLinkVariantId : selectedVariantId);
        }) || variants[0];

        var preferredVariant = null;
        if (!isDeepLink && preferredBand && preferredCup && variants.length) {
          var currentColor = selectedVariant ? selectedVariant.option1 : variants[0].option1;
          var preferredSize = preferredBand + preferredCup;

          preferredVariant = variants.find(function (v) {
            return v.option1 === currentColor && v.option2 === preferredSize;
          });

          if (!preferredVariant) {
            preferredVariant = variants.find(function (v) {
              return v.option2 === preferredSize;
            });
          }

          if (preferredVariant) {
            selectedVariant = preferredVariant;
          }
        }

        /*
          PDP-10: with no stored preference this fell through to variants[0] and
          preselected a band and cup on a completely fresh visit — the bra half of
          "no size selected by default". Only apply a band/cup the shopper actually
          chose before, or one a deep link named explicitly (?variant= that did not
          come from a product card, i.e. a shared link — PDP-24).
        */
        // preferredVariant is only set when the remembered band+cup actually exists on this
        // product; without that check a shopper who saved 32B and opened a bra with no 32B
        // got variants[0] (34D) preselected — a size nobody chose.
        var matchedPreference = !!preferredVariant;

        if ((matchedPreference || isDeepLink) && selectedVariant && selectedVariant.option2) {
          var combinedSize = selectedVariant.option2;
          var bandMatch = combinedSize.match(/^(\d+)/);
          var cupMatch = combinedSize.match(/([A-Z]+)$/);

          if (bandMatch && cupMatch) {
            var band = bandMatch[1];
            var cup = cupMatch[1];

            $(scope).find('.product__chip[data-type="band"]').removeClass('selected');
            $(scope).find('.product__chip[data-type="cup"]').removeClass('selected');
            $(scope).find('.product__chip[data-type="band"][data-value="' + band + '"]').addClass('selected');
            $(scope).find('.product__chip[data-type="cup"][data-value="' + cup + '"]').addClass('selected');

            $(scope).find('[data-bandsize-value]').text(band);
            $(scope).find('[data-cupsize-value]').text(cup);

            var $option1Select = $(scope).find('#option1');
            if ($option1Select.length && selectedVariant.option1 && $option1Select.val() !== selectedVariant.option1) {
              $option1Select.val(selectedVariant.option1);
            }

            var $option2Select = $(scope).find('[name="options[Size]"]');
            if ($option2Select.length && $option2Select.val() !== combinedSize) {
              $option2Select.val(combinedSize);
              $option2Select.trigger('change');
              if ($option2Select[0]) $option2Select[0].dispatchEvent(new Event('change', { bubbles: true }));
            }

            var $combinedButton = $(scope).find('.chips2-wrap .product__chip[data-value="' + combinedSize + '"]');
            if ($combinedButton.length) {
              $combinedButton.trigger('click');
            }

            var $variantSelector = $(scope).find('#variant-selector, select[name="id"]');
            if ($variantSelector.length && $variantSelector.val() != selectedVariant.id) {
              $variantSelector.val(selectedVariant.id);
              if ($variantSelector[0]) $variantSelector[0].dispatchEvent(new Event('change', { bubbles: true }));
            }

            manageError();
          }
        }
      } catch (e) {
        console.error('Error in restoreBraSizePreference:', e);
      }
    }
  }

  function restoreApparelSizePreference() {
    var rawType = ($('.product__meta #currproduct-type').val() || $('#currproduct-type').val() || '').toLowerCase();
    var category = getNormalizedSizeCategory(rawType);
    if (category === 'bra') return;

    var categoryKey = 'felina_apparel_size_preference';
    if (category === 'panty') {
      categoryKey = 'felina_panty_size_preference';
    } else if (category === 'simple_bra') {
      categoryKey = 'felina_simple_bra_size_preference';
    }

    var savedSize =
      localStorage.getItem(categoryKey) ||
      localStorage.getItem('felina_apparel_size_preference') ||
      (rawType ? localStorage.getItem(rawType.replace(' ', '_')) : null);

    if (!savedSize || savedSize === 'undefined' || savedSize === 'null') return;

    try {
      if (typeof savedSize === 'string' && (savedSize.startsWith('{') || savedSize.startsWith('['))) {
        var parsed = JSON.parse(savedSize);
        if (typeof parsed === 'object' && parsed !== null) {
          savedSize = parsed.size || parsed.Size || Object.values(parsed)[0];
        }
      }
    } catch (e) {}

    if (!savedSize || typeof savedSize !== 'string') return;
    savedSize = savedSize.trim();

    // 1. Locate and select matching chip in DOM
    var $chips = $('.product__chip:not(.band-btn):not(.cup-btn)');
    var $matchedChip = null;

    $chips.each(function () {
      var chipVal = $(this).attr('data-option-value') || $(this).data('value') || $(this).text().trim();
      if (sizesMatch(chipVal, savedSize)) {
        $matchedChip = $(this);
        return false;
      }
    });

    if ($matchedChip && $matchedChip.length) {
      var chipVal = $matchedChip.attr('data-option-value') || $matchedChip.data('value') || $matchedChip.text().trim();

      // Trigger Stiletto's OptionButtons handler so its internal state, buttons, and select sync
      if (!$matchedChip.hasClass('selected')) {
        $matchedChip[0].click();
      }

      $matchedChip.siblings('.product__chip').removeClass('selected');
      $matchedChip.addClass('selected');

      var $label = $matchedChip.closest('.product__option').find('[data-selected-value-for-option]');
      if ($label.length && chipVal) {
        $label.text(chipVal);
      }

      var $parent = $matchedChip.closest('.product__color-chips, .select-wrapper, [data-option-buttons]');
      var $select = $parent.find('select');
      if ($select.length && chipVal) {
        if ($select.val() !== chipVal) {
          $select.val(chipVal);
        }
        $select.find('option').each(function () {
          if (sizesMatch($(this).val(), chipVal)) {
            $(this).prop('selected', true);
            return false;
          }
        });
        $select.trigger('change');
        if ($select[0]) $select[0].dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    // 2. Also inspect select dropdowns if chips aren't used
    $('select[name^="options[Size]"], select[name^="options[size]"], select.dynamic-variant-input').each(function () {
      var $sel = $(this);
      if ($sel.attr('id') === 'option1' && $('.product__color-swatch').length) return;
      $sel.find('option').each(function () {
        var optVal = $(this).val();
        if (sizesMatch(optVal, savedSize)) {
          if ($sel.val() !== optVal) {
            $sel.val(optVal);
            $(this).prop('selected', true);
            $sel.trigger('change');
            if ($sel[0]) $sel[0].dispatchEvent(new Event('change', { bubbles: true }));
          }
          return false;
        }
      });
    });

    // 3. Update size option label text
    var $sizeLabels = $('[data-selected-value-for-option]').filter(function () {
      var optName = $(this).closest('.product__option').find('label').text().toLowerCase();
      return optName.includes('size');
    });
    if ($sizeLabels.length && savedSize) {
      var labelVal = $matchedChip && $matchedChip.length ? ($matchedChip.attr('data-option-value') || $matchedChip.text().trim()) : savedSize;
      $sizeLabels.text(labelVal);
    }

    // 4. Sync variant selector and data-variant-json
    var variantsJsonEl = $('[data-variant-json]');
    if (variantsJsonEl.length) {
      try {
        var variants = JSON.parse(variantsJsonEl.text());
        var selectedVariantId = $('#variant-selector').val() || $('select[name="id"]').val();
        var currentVariant = variants.find(function (v) {
          return v.id == selectedVariantId;
        }) || variants[0];

        var currentColor = currentVariant ? currentVariant.option1 : null;

        var matchedVariant = variants.find(function (v) {
          var colorMatches = currentColor ? v.option1 === currentColor : true;
          var sizeMatches = sizesMatch(v.option1, savedSize) || sizesMatch(v.option2, savedSize) || sizesMatch(v.option3, savedSize);
          return colorMatches && sizeMatches && isBraVariantAvailable(v);
        });

        if (!matchedVariant) {
          matchedVariant = variants.find(function (v) {
            var colorMatches = currentColor ? v.option1 === currentColor : true;
            return colorMatches && (sizesMatch(v.option1, savedSize) || sizesMatch(v.option2, savedSize) || sizesMatch(v.option3, savedSize));
          });
        }

        if (!matchedVariant) {
          matchedVariant = variants.find(function (v) {
            return sizesMatch(v.option1, savedSize) || sizesMatch(v.option2, savedSize) || sizesMatch(v.option3, savedSize);
          });
        }

        if (matchedVariant) {
          var $variantSelector = $('#variant-selector, select[name="id"]');
          if ($variantSelector.length && $variantSelector.val() != matchedVariant.id) {
            $variantSelector.val(matchedVariant.id);
            if ($variantSelector[0]) $variantSelector[0].dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      } catch (e) {
        console.error('Error restoring apparel size variant:', e);
      }
    }

    $('[no-size-error]').hide();
    manageError();
  }

  function runAllSizePreferenceRestorations() {
    // Once the shopper has chosen a size, neither the clear nor the restore may touch it.
    // The restore path stomps a fresh choice with a remembered one just as readily as the
    // clear path wipes it.
    if (sizeChoiceLocked || window.__userSelectedSizeManually) return;
    var gridModal = activeGridModal();
    if (gridModal) {
      clearSizeSelection(gridModal);
      return;
    }
    // PDP-10: arriving from a product card must leave the size unchosen, so neither the
    // ?variant= in the card link nor a remembered size may select one. Every pass clears
    // instead of restoring, which also beats the theme's own variant engine re-selecting.
    if (isGridReferral()) {
      clearSizeSelection();
      return;
    }
    restoreBraSizePreference();
    restoreApparelSizePreference();
  }

  window.restoreBraSizePreference = restoreBraSizePreference;
  window.restoreApparelSizePreference = restoreApparelSizePreference;
  window.runAllSizePreferenceRestorations = runAllSizePreferenceRestorations;

  // Multi-pass execution to avoid race conditions with Stiletto's variant engine
  setTimeout(runAllSizePreferenceRestorations, 50);
  setTimeout(runAllSizePreferenceRestorations, 150);
  setTimeout(function () {
    updateBraSizeAvailability();
    runAllSizePreferenceRestorations();
  }, 350);
  setTimeout(function () {
    updateBraSizeAvailability();
    runAllSizePreferenceRestorations();
    manageError();
  }, 650);
  setTimeout(function () {
    updateBraSizeAvailability();
    runAllSizePreferenceRestorations();
    manageError();
    if (isGridReferral()) {
      window.__initialGridReferralHandled = true;
      stripGridReferralFromUrl();
    }
  }, 1200);

  $(window).on('load', function () {
    setTimeout(function () {
      updateBraSizeAvailability();
      runAllSizePreferenceRestorations();
      manageError();
    }, 100);
    setTimeout(function () {
      updateBraSizeAvailability();
      runAllSizePreferenceRestorations();
      manageError();
      syncAllAcceleratedCheckoutVariants(4);
      if (isGridReferral()) {
        window.__initialGridReferralHandled = true;
        stripGridReferralFromUrl();
      }
    }, 400);
  });

  // Listen for variant changes from the select elements (triggered by OptionButtons)
  $(document).on('change', '.dynamic-variant-input', function () {
    var changedSelect = $(this);
    var selectId = changedSelect.attr('id');

    // Check if this is option1 (color) changing and the product actually has a color option
    var hasColor = $('.product__variants-wrapper').data('has-color');
    if (hasColor === undefined) {
      hasColor = $('.product__color-swatch, .product__chip[data-type="color"]').length > 0;
    }
    if (selectId === 'option1' && hasColor) {
      var selectedColor = changedSelect.val();

      // Get all product variants from JSON
      var variantsJsonEl = $('[data-variant-json]');
      if (variantsJsonEl.length && selectedColor) {
        try {
          var variants = JSON.parse(variantsJsonEl.text());

          // Find all available variants for the selected color
          var availableVariantsForColor = variants.filter(function (variant) {
            return variant.option1 === selectedColor && isBraVariantAvailable(variant);
          });

          // Check if a size is currently chosen
          var hasChosenSize = false;
          if ($('#currproduct-type[value*="Bra"]').length) {
            hasChosenSize = $('.product__chip[data-type="band"].selected').length > 0 && $('.product__chip[data-type="cup"].selected').length > 0;
          } else {
            hasChosenSize = $('.product__chip.selected:not([data-type="color"])').length > 0;
          }

          // If no size is chosen yet (PDP-10), leave the size unchosen on color switch!
          if (!hasChosenSize) {
            clearSizeSelection();
            updateBraSizeAvailability();
            manageError();
            return;
          }

          // If there are available variants for this color, select the best match
          if (availableVariantsForColor.length > 0) {
            var firstAvailableVariant = availableVariantsForColor[0];

            // For bra products, handle band/cup selection
            if ($('#currproduct-type[value*="Bra"]').length) {
              var currentBand = $('.product__chip[data-type="band"].selected').data('value') || $('[data-bandsize-value]').text().trim();
              var currentCup = $('.product__chip[data-type="cup"].selected').data('value') || $('[data-cupsize-value]').text().trim();
              var targetBraVariant = null;
              if (currentBand && currentCup) {
                var targetSize = currentBand + currentCup;
                targetBraVariant = availableVariantsForColor.find(function (v) {
                  return v.option2 === targetSize;
                });
              }

              if (targetBraVariant) {
                var $variantSelector = $('#variant-selector, select[name="id"]');
                if ($variantSelector.length && $variantSelector.val() != targetBraVariant.id) {
                  $variantSelector.val(targetBraVariant.id);
                }
              }
              updateBraSizeAvailability();
              manageError();
            } else {
              // For regular products (sleepwear, loungewear, apparel, panties, etc.)
              var rawType = ($('#currproduct-type').val() || '').toLowerCase();
              var category = getNormalizedSizeCategory(rawType);
              var categoryKey = 'felina_apparel_size_preference';
              if (category === 'panty') categoryKey = 'felina_panty_size_preference';
              else if (category === 'simple_bra') categoryKey = 'felina_simple_bra_size_preference';

              var currentSelectedSize = $('.product__chip.selected:not(.band-btn):not(.cup-btn)').attr('data-option-value') ||
                                        $('.product__chip.selected:not(.band-btn):not(.cup-btn)').text().trim() ||
                                        localStorage.getItem(categoryKey) ||
                                        localStorage.getItem('felina_apparel_size_preference');

              var targetVariant = null;
              if (currentSelectedSize && availableVariantsForColor.length > 0) {
                targetVariant = availableVariantsForColor.find(function (v) {
                  return sizesMatch(v.option1, currentSelectedSize) || sizesMatch(v.option2, currentSelectedSize) || sizesMatch(v.option3, currentSelectedSize);
                });
              }

              if (targetVariant) {
                var $variantSelector = $('#variant-selector, select[name="id"]');
                if ($variantSelector.length && $variantSelector.val() != targetVariant.id) {
                  $variantSelector.val(targetVariant.id);
                }

                var chosenSize = targetVariant.option2 || targetVariant.option1;
                var option2Select = $('[name^="options[Size]"], [name^="options[size]"]');
                if (option2Select.length && chosenSize) {
                  option2Select.val(chosenSize);
                  option2Select.trigger('change');
                  if (option2Select[0]) option2Select[0].dispatchEvent(new Event('change', { bubbles: true }));
                }

                var chosenChip = null;
                $('.product__chip:not(.band-btn):not(.cup-btn)').each(function () {
                  var val = $(this).attr('data-option-value') || $(this).data('value') || $(this).text().trim();
                  if (sizesMatch(val, chosenSize)) {
                    chosenChip = this;
                    return false;
                  }
                });
                if (chosenChip && !$(chosenChip).hasClass('selected')) {
                  chosenChip.click();
                } else if (chosenChip) {
                  $(chosenChip).siblings('.product__chip').removeClass('selected');
                  $(chosenChip).addClass('selected');
                }

                var $colorSizeLabel = $('[data-selected-value-for-option]').filter(function () {
                  var optName = $(this).closest('.product__option').find('label').text().toLowerCase();
                  return optName.includes('size');
                });
                if ($colorSizeLabel.length && chosenSize) {
                  $colorSizeLabel.text(chosenSize);
                }
              }
            }
          } else {
            if ($('#currproduct-type[value*="Bra"]').length) {
              $('.product__chip[data-type="band"]').removeClass('selected');
              $('.product__chip[data-type="cup"]').removeClass('selected');
              $('[data-bandsize-value]').text('');
              $('[data-cupsize-value]').text('');

              var productType = ($('#currproduct-type').val() || '').toLowerCase().replace(' ', '_');
              var storageKey = productType.replace(' ', '_');
              localStorage.removeItem(storageKey);

              $('[data-band-error]').show();
              $('[data-cup-error]').show();
            }
          }
        } catch (e) {
          console.error('Error in color change handler:', e);
        }
      }
    }

    updateBraSizeAvailability();
    setTimeout(function () {
      updateBraSizeAvailability();
      manageError();
    }, 200);
  });

  $(document).on('click', '.product__chip', function (e) {
    // Scope every lookup below to whichever product this chip actually belongs to — the
    // open quick-view modal if it's inside one, otherwise the page itself. A bare $(selector)
    // here would also match a second product's markup (a quick view opened over a PDP's own
    // form, e.g. from "You may also like") and silently mix the two products' data.
    var scope = $(this).closest('[data-quick-view-modal]')[0] || document;
    var optionType = $(this).data('type');
    var optionValue = $(this).attr('data-option-value') || $(this).data('value') || $(this).text().trim();
    var productType = ($(scope).find('#currproduct-type').val() || '').toLowerCase();
    var storageKey = productType.replace(' ', '_');

    if (optionType !== 'color') {
      window.__userSelectedSizeManually = true;
      window.__initialGridReferralHandled = true;
      stripGridReferralFromUrl();
    }

    var isComplexBra = productType.includes('bra') && $(scope).find('.product__chip[data-type="band"]').length > 0;

    if (!isComplexBra) {
      $(scope).find('[no-size-error]').hide();
    }

    if (isComplexBra) {
      if (optionType === 'band' || optionType === 'cup') {
        $(scope).find('.product__chip[data-type="' + optionType + '"]').removeClass('selected');
        $(this).addClass('selected');
        updateBraSizeAvailability();
      }

      var savedPref = localStorage.getItem('felina_bra_size_preference') || localStorage.getItem(storageKey);
      var braOptions = {};
      if (savedPref) {
        try {
          braOptions = typeof savedPref === 'string' && (savedPref.startsWith('{') || savedPref.startsWith('[')) ? JSON.parse(savedPref) : {};
        } catch (err) {}
      }

      if (optionType === 'band') {
        $(scope).find('[data-bandsize-value]').text(optionValue);
      } else if (optionType === 'cup') {
        $(scope).find('[data-cupsize-value]').text(optionValue);
      }

      // Send the theme the band and cup showing on screen. Taking the other half from the
      // remembered size put a size nobody picked into the cart: with 36D remembered from
      // another bra and a link opening on 34C, a tap on 36 showed 36 / C but added 36D.
      var $braPicker = $(this).closest('[data-product-bra]');
      if (!$braPicker.length) $braPicker = $(scope);
      var bandOnScreen = getBraChipValue($braPicker.find('.product__chip[data-type="band"].selected').first());
      var cupOnScreen = getBraChipValue($braPicker.find('.product__chip[data-type="cup"].selected').first());
      if (bandOnScreen) braOptions['band'] = bandOnScreen;
      if (cupOnScreen) braOptions['cup'] = cupOnScreen;

      localStorage.setItem('felina_bra_size_preference', JSON.stringify(braOptions));
      localStorage.setItem(storageKey, JSON.stringify(braOptions));

      if (bandOnScreen && cupOnScreen) {
        var combinedSize = bandOnScreen + cupOnScreen;
        var $option2Select = $(scope).find('[name="options[Size]"]');
        if ($option2Select.length) {
          $option2Select.val(combinedSize);
          $option2Select.trigger('change');
          if ($option2Select[0]) $option2Select[0].dispatchEvent(new Event('change', { bubbles: true }));
        }

        setTimeout(function () {
          manageError();
        }, 100);
      } else {
        manageError();
      }
    } else {
      window.__userSelectedSizeManually = true;
      window.__initialGridReferralHandled = true;
      stripGridReferralFromUrl();

      // Non-bra products (sleepwear, loungewear, panties, apparel, simple bras)
      $(this).siblings('.product__chip').removeClass('selected');
      $(this).addClass('selected');

      var $parent = $(this).closest('.product__color-chips, .select-wrapper, [data-option-buttons]');
      var $select = $parent.find('select');

      if ($select.length && optionValue && $select.val() !== optionValue) {
        $select.val(optionValue);
        $select.trigger('change');
        if ($select[0]) $select[0].dispatchEvent(new Event('change', { bubbles: true }));
      }

      var category = getNormalizedSizeCategory(productType);
      var categoryKey = 'felina_apparel_size_preference';
      if (category === 'panty') {
        categoryKey = 'felina_panty_size_preference';
      } else if (category === 'simple_bra') {
        categoryKey = 'felina_simple_bra_size_preference';
      }

      if (optionValue) {
        localStorage.setItem(categoryKey, optionValue);
        localStorage.setItem('felina_apparel_size_preference', optionValue);
      }

      var options = {};
      options[optionType || 'size'] = optionValue;
      localStorage.setItem(storageKey, JSON.stringify(options));
      updateBraSizeAvailability();
      manageError();
    }
  });

  // When size dropdown changes, also mark manual selection and clean ref=grid
  $(document).on('change', 'select[name^="options[Size]"], select[name^="options[size]"], select[data-index="option2"]', function () {
    window.__userSelectedSizeManually = true;
    window.__initialGridReferralHandled = true;
    stripGridReferralFromUrl();
  });

  // Save current size selection before navigating to a sibling product
  $(document).on('click', '[data-sibling-swatch], a.product__color-swatch--sibling-product, a.product-color__link', function () {
    var productType = ($('#currproduct-type').val() || '').toLowerCase();

    if (productType.includes('bra') && $('.product__chip[data-type="band"]').length > 0) {
      var selectedBand = $('.product__chip[data-type="band"].selected').attr('data-option-value') ||
                         $('.product__chip[data-type="band"].selected').data('value') ||
                         $('[data-bandsize-value]').text().trim();
      var selectedCup = $('.product__chip[data-type="cup"].selected').attr('data-option-value') ||
                        $('.product__chip[data-type="cup"].selected').data('value') ||
                        $('[data-cupsize-value]').text().trim();
      if (selectedBand && selectedCup) {
        localStorage.setItem(
          'felina_bra_size_preference',
          JSON.stringify({
            band: selectedBand,
            cup: selectedCup
          })
        );
      }
    } else {
      var $selectedChip = $('.product__chip.selected:not(.band-btn):not(.cup-btn)');
      var selectedVal = $selectedChip.attr('data-option-value') || $selectedChip.data('value') || $selectedChip.text().trim();
      if (!selectedVal) {
        var $sel = $('select[name^="options[Size]"], select[name^="options[size]"]');
        if ($sel.length) selectedVal = $sel.val();
      }
      if (selectedVal) {
        var category = getNormalizedSizeCategory(productType);
        var categoryKey = 'felina_apparel_size_preference';
        if (category === 'panty') {
          categoryKey = 'felina_panty_size_preference';
        } else if (category === 'simple_bra') {
          categoryKey = 'felina_simple_bra_size_preference';
        }
        localStorage.setItem(categoryKey, selectedVal);
        localStorage.setItem('felina_apparel_size_preference', selectedVal);
      }
    }
  });
});

  // ═══════════════════════════════════════════════════════════════════════════
  // 5.5 KLAVIYO BIS TRIGGER — NEUTRALIZE KLAVIYO'S OWN BROKEN REVEAL
  //
  //    Klaviyo's onsite script reacts to the native "change" event dispatched below
  //    by redrawing its own BIS trigger, but its internal show() sets display:inline
  //    instead of the button's real display:flex. overflow:hidden (which normally
  //    clips the button's decorative skewed ::before hover-fill so it looks like a
  //    plain rectangle) has no effect on a display:inline box, so for the ~300-400ms
  //    until executeManageError()'s own .css('display', ...) call corrects it, the
  //    button visibly flashes as a slanted ribbon. executeManageError() is the only
  //    thing in this theme that should own this element's visibility, so watch for
  //    Klaviyo writing that bad value and strip it before the browser paints it.
  // ═══════════════════════════════════════════════════════════════════════════
  new MutationObserver(function(mutations) {
    mutations.forEach(function(mutation) {
      var target = mutation.target;
      if (
        target.classList &&
        target.classList.contains('klaviyo-bis-trigger-btn') &&
        target.style.display &&
        target.style.display !== 'none'
      ) {
        target.style.removeProperty('display');
      }
    });
  }).observe(document.body, { attributes: true, attributeFilter: ['style'], subtree: true });

  // ═══════════════════════════════════════════════════════════════════════════
  // 6. KLAVIYO BACK IN STOCK DYNAMIC VARIANT UPDATE
  //
  //    Listens for Stiletto's custom "product:variant-change" event and triggers
  //    a standard "change" event on the hidden master variant selector ([name="id"]).
  //    Klaviyo's onsite script binds its own listener to that select (during the
  //    one-time klaviyo.enable() call in theme.liquid) and shows/hides its own BIS
  //    trigger from there — don't call klaviyo.enable() again here, it breaks the
  //    widget instead of refreshing it (see theme.liquid).
  // ═══════════════════════════════════════════════════════════════════════════
  document.addEventListener("product:variant-change", function(event) {
    setTimeout(function() {
      // Runs after the theme has re-enabled buttons and reset the buy button for the
      // new variant, so this pass has the final say on what is disabled.
      updateBraSizeAvailability();
      manageError();

      // 1. Force a change event on variant select inputs so that Klaviyo's internal event delegation is notified
      var selectEls = document.querySelectorAll('select[name="id"], [data-variant-select]');
      selectEls.forEach(function(selectEl) {
        selectEl.dispatchEvent(new Event('change', { bubbles: true }));
      });

      // Shopify's accelerated checkout listens to the product form itself.
      // Notify it after the theme and the custom size logic agree on the final variant.
      syncAllAcceleratedCheckoutVariants(2);

      // 2. Toggle Comparable Value Tooltip visibility based on active variant compare-at price
      var comparePriceEl = document.querySelector('[data-compare-price]');
      var tooltipEl = document.querySelector('.comparable-value-tooltip');
      if (comparePriceEl && tooltipEl) {
        var hasComparePrice = comparePriceEl.textContent.trim() !== '';
        if (hasComparePrice) {
          tooltipEl.style.setProperty('display', 'flex', 'important');
        } else {
          tooltipEl.style.setProperty('display', 'none', 'important');
        }
      }
    }, 250); // 250ms delay to make absolutely sure Stiletto's theme.js has finished updating the DOM
  });

  // ── Sizing Help popup toggle and tab switching ─────────────────────────────────
  $(document).on('click', '.size-guide-button', function(e) {
    e.preventDefault();
    $('.size-chart-pop-up-wrapper').show();
    $('.size-popup-close').show();
  });

  $(document).on('click', '.size-chart-pop-up-panel__close, .size-popup-close, [data-js-popup-close]', function(e) {
    e.preventDefault();
    $('.size-chart-pop-up-wrapper').hide();
    $('.size-popup-close').hide();
  });

  $(document).on('click', '[data-parent]', function() {
    var parentId = $(this).attr('data-parent');

    // Toggle active state for tab buttons
    $('[data-parent]').removeClass('active');
    $(this).addClass('active');

    // Toggle active state for tab panels
    $('[data-child]').removeClass('active');
    $('[data-child="' + parentId + '"]').addClass('active');
  });

  // ═══════════════════════════════════════════════════════════════════════════
  // 7. PRELOAD PRODUCT GALLERY IMAGES
  //
  //    Preloads lazy-loaded images in the product media gallery so that switching
  //    between thumbnails does not cause a blank/white flash. Once preloaded,
  //    it adds the "loaded" class to their parent wrapper to enable instant visibility.
  // ═══════════════════════════════════════════════════════════════════════════
  function preloadGalleryImages() {
    var lazyImages = document.querySelectorAll('[data-product-media-wrapper] img[loading="lazy"]');
    lazyImages.forEach(function (img) {
      if (img.complete) {
        var parentImage = img.closest('.image');
        if (parentImage) {
          parentImage.classList.add('loaded');
        }
        return;
      }

      var preloadImg = new Image();
      if (img.sizes) preloadImg.sizes = img.sizes;
      if (img.srcset) preloadImg.srcset = img.srcset;
      preloadImg.src = img.src;

      preloadImg.onload = function () {
        var parentImage = img.closest('.image');
        if (parentImage) {
          parentImage.classList.add('loaded');
        }
      };
    });
  }

  // Run on page load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', preloadGalleryImages);
  } else {
    preloadGalleryImages();
  }
  window.addEventListener('load', preloadGalleryImages);

  // Hook into variant changes (which replaces gallery elements in the DOM)
  document.addEventListener("product:variant-change", function() {
    setTimeout(preloadGalleryImages, 250);
  });

  // ═══════════════════════════════════════════════════════════════════════════
  // 8. ANNOUNCEMENT BAR LINK CLICK & INERT FIX
  //
  //    Fixes an issue where Swiper's loop mode and inert attribute prevented
  //    clicks on announcement links on desktop (causing clicks to swipe instead
  //    of navigating to the target URL).
  // ═══════════════════════════════════════════════════════════════════════════
  function fixAnnouncementBarLinks() {
    var announcementBar = document.querySelector('.announcement-bar');
    if (!announcementBar) return;

    function cleanInertOnActive() {
      announcementBar.querySelectorAll('.swiper-slide-active, .swiper-slide-active *').forEach(function (el) {
        el.removeAttribute('inert');
      });
    }

    cleanInertOnActive();

    if (typeof MutationObserver !== 'undefined') {
      var observer = new MutationObserver(cleanInertOnActive);
      observer.observe(announcementBar, { attributes: true, subtree: true, attributeFilter: ['class', 'inert'] });
    }
  }

  // Direct click handler for announcement links to guarantee navigation
  document.addEventListener('click', function (e) {
    var link = e.target.closest && e.target.closest('.announcement-bar a.announcement-bar__link, .announcement-bar .swiper-slide a');
    if (!link) return;

    var href = link.getAttribute('href');
    if (href && href !== '#' && !href.startsWith('javascript:')) {
      window.location.href = href;
    }
  }, true);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', fixAnnouncementBarLinks);
  } else {
    fixAnnouncementBarLinks();
  }
  window.addEventListener('load', fixAnnouncementBarLinks);

  // Ensure image-hero images (especially mobile responsive banners) have .loaded added when complete
  function checkHeroImagesLoaded() {
    var heroImages = document.querySelectorAll('.image-hero .image img, .image-hero__image img');
    heroImages.forEach(function (img) {
      var imageWrapper = img.closest('.image');
      if (imageWrapper && !imageWrapper.classList.contains('loaded')) {
        if (img.complete && img.naturalWidth > 0) {
          imageWrapper.classList.add('loaded');
        } else {
          img.addEventListener('load', function () {
            imageWrapper.classList.add('loaded');
          }, { once: true });
        }
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', checkHeroImagesLoaded);
  } else {
    checkHeroImagesLoaded();
  }
  window.addEventListener('load', checkHeroImagesLoaded);
  window.addEventListener('resize', checkHeroImagesLoaded);

})();
